import type { RoomPlaybackSnapshot } from '@crowdset/contracts';
import {
  desiredPositionMs,
  DriftController,
  msUntilStart,
  type PlaybackTiming,
  type PlayerState,
} from '@crowdset/domain';
import type { PlayerAdapter } from './types';

export type SyncStatus =
  | 'idle' // room has nothing playing
  | 'needs-consent' // waiting for the user to press Enable playback (or the player to be visible)
  | 'loading'
  | 'waiting' // track is scheduled to start shortly
  | 'synced'
  | 'buffering'
  | 'unsynced' // user paused/seeked; following suspended until Sync to room
  | 'blocked' // browser blocked autoplay; needs a gesture
  | 'suspended' // tab was hidden; local playback paused, offer resync
  | 'error' // local player error (does not affect the room)
  | 'finished'; // local player reached the end before the room advanced

export interface SyncView {
  status: SyncStatus;
  following: boolean;
  errorCode: number | null;
  driftMs: number | null;
  playId: string | null;
}

export type StartPlan =
  | { kind: 'idle' }
  | { kind: 'wait'; waitMs: number }
  | { kind: 'play'; positionMs: number }
  | { kind: 'ended' };

/** Where should a follower be right now? Pure; unit-tested. */
export function planStart(t: PlaybackTiming, serverNowMs: number): StartPlan {
  const desired = desiredPositionMs(t, serverNowMs);
  if (desired === null) return { kind: 'idle' };
  const wait = msUntilStart(t, serverNowMs);
  if (wait > 0) return { kind: 'wait', waitMs: wait };
  if (t.durationMs !== null && desired >= t.durationMs) return { kind: 'ended' };
  return { kind: 'play', positionMs: desired };
}

export interface SyncOptions {
  adapter: PlayerAdapter;
  serverNow(): number;
  localNow(): number;
  isVisible(): boolean;
  report(
    kind: 'error' | 'autoplay_blocked' | 'unsynced' | 'synced',
    playId: string,
    errorCode?: number,
  ): void;
  /** How long a state change after our own command is attributed to us, not the user. */
  expectWindowMs?: number;
  /** Drift beyond this right after an unexpected play is treated as a user seek. */
  userSeekThresholdMs?: number;
}

interface CurrentPlay {
  playId: string;
  videoId: string;
  status: RoomPlaybackSnapshot['status'];
  startsAtMs: number;
  durationMs: number;
}

/**
 * Follows the authoritative room clock with one local player. Local pause/seek/error never
 * affect the room; this class only ever reports diagnostics. Never changes playback rate.
 */
export class PlaybackSync {
  private play: CurrentPlay | null = null;
  private loadedVideo: string | null = null;
  private consent = false;
  private inView = true;
  private expectUntil = 0;
  /** Whether our own most recent command was a pause/stop (so a 'paused' event is ours). */
  private pauseExpected = false;
  private reportedBlocked: string | null = null;
  private drift = new DriftController();
  private ticks = 0;
  private lastSample: { pos: number; at: number } | null = null;
  private listeners = new Set<() => void>();
  private _view: SyncView = {
    status: 'idle',
    following: true,
    errorCode: null,
    driftMs: null,
    playId: null,
  };

  constructor(private readonly o: SyncOptions) {}

  /**
   * Swap the underlying player (e.g. moving into or out of the pop-out window). The new player is
   * empty, so reload the current track at the room position if we were playing along.
   */
  replaceAdapter(adapter: SyncOptions['adapter']) {
    this.o.adapter = adapter;
    this.loadedVideo = null;
    this.drift.reset();
    this.lastSample = null;
    if (!this.play) return;
    if (this.consent && this._view.following) this.syncNow();
    else if (this.consent) this.syncToRoom();
  }

  get adapter() {
    return this.o.adapter;
  }

  get view(): SyncView {
    return this._view;
  }
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  getView = () => this._view;

  private setView(p: Partial<SyncView>) {
    this._view = { ...this._view, ...p };
    for (const l of this.listeners) l();
  }

  private timing(): PlaybackTiming {
    if (!this.play) return { status: 'idle', startsAtMs: null, durationMs: null };
    return {
      status: this.play.status,
      startsAtMs: this.play.startsAtMs,
      durationMs: this.play.durationMs,
    };
  }

  private expect(pausing = false) {
    this.expectUntil = this.o.localNow() + (this.o.expectWindowMs ?? 2_000);
    this.pauseExpected = pausing;
  }
  private expecting() {
    return this.o.localNow() < this.expectUntil;
  }

  /** Feed the authoritative playback snapshot (from snapshot or playback.changed). */
  update(pb: RoomPlaybackSnapshot | null) {
    if (
      !pb ||
      pb.status === 'idle' ||
      !pb.playId ||
      !pb.videoId ||
      pb.startsAtMs === null ||
      pb.durationMs === null
    ) {
      if (this.play) {
        this.play = null;
        this.loadedVideo = null;
        this.expect(true);
        this.o.adapter.stop();
      }
      this.setView({
        status: 'idle',
        playId: null,
        errorCode: null,
        driftMs: null,
        following: true,
      });
      return;
    }
    const isNew = this.play?.playId !== pb.playId;
    this.play = {
      playId: pb.playId,
      videoId: pb.videoId,
      status: pb.status,
      startsAtMs: pb.startsAtMs,
      durationMs: pb.durationMs,
    };
    if (isNew) this.onNewPlay();
  }

  private onNewPlay() {
    this.drift.reset();
    this.lastSample = null;
    this.setView({ playId: this.play!.playId, following: true, errorCode: null, driftMs: null });
    if (!this.consent || !this.inView) {
      const plan = planStart(this.timing(), this.o.serverNow());
      this.loadedVideo = this.play!.videoId;
      this.expect();
      this.o.adapter.load(
        this.play!.videoId,
        plan.kind === 'play' ? plan.positionMs : 0,
        false,
        this.play!.durationMs,
      );
      this.setView({ status: 'needs-consent' });
      return;
    }
    this.syncNow();
  }

  /** User gesture: allow playback in this page session and start following. */
  enable() {
    this.consent = true;
    this.syncToRoom();
  }

  get hasConsent() {
    return this.consent;
  }

  /** User gesture: resume following the room clock. */
  syncToRoom() {
    this.drift.reset();
    this.lastSample = null;
    this.setView({ following: true, errorCode: null });
    this.syncNow();
  }

  setInView(v: boolean) {
    const was = this.inView;
    this.inView = v;
    if (v && !was && this.consent && this._view.status === 'needs-consent') this.syncNow();
  }

  /** Document visibility changed. Hidden => suspend local playback (no background-only audio). */
  onVisibility(visible: boolean) {
    if (!visible) {
      if (
        this.play &&
        ['synced', 'buffering', 'loading', 'unsynced', 'waiting'].includes(this._view.status)
      ) {
        this.expect(true);
        this.o.adapter.pause();
        this.setView({ status: 'suspended' });
      }
    }
    // On return we deliberately wait for the user to press Sync (fresh snapshot first).
  }

  private syncNow() {
    if (!this.play) {
      this.setView({ status: 'idle' });
      return;
    }
    const plan = planStart(this.timing(), this.o.serverNow());
    const a = this.o.adapter;
    this.expect();
    switch (plan.kind) {
      case 'idle':
        this.setView({ status: 'idle' });
        return;
      case 'ended':
        this.setView({ status: 'finished' });
        return;
      case 'wait':
        this.setView({ status: 'waiting' });
        // Never pause/seek a player that has not started: the YouTube embed rejects seekTo on a
        // cued-but-unplayed video (error 2). Re-cue instead, and only when needed.
        if (this.loadedVideo !== this.play.videoId || !this.isFresh()) {
          a.load(this.play.videoId, 0, false, this.play.durationMs);
          this.loadedVideo = this.play.videoId;
        }
        return;
      case 'play':
        // Status first: adapters may report 'playing' synchronously from play().
        this.setView({ status: 'loading' });
        if (this.loadedVideo !== this.play.videoId || this.isFresh()) {
          // Load-and-play from the position in one call; a cued, never-played embed must not
          // be seeked first.
          a.load(this.play.videoId, plan.positionMs, true, this.play.durationMs);
          this.loadedVideo = this.play.videoId;
        } else {
          a.seek(plan.positionMs);
          a.play();
        }
    }
  }

  /** The player holds the current video but has never started playing it. */
  private isFresh(): boolean {
    const st = this.o.adapter.state();
    return st === 'cued' || st === 'unstarted';
  }

  /** Call about once per second while mounted. Starts scheduled plays and checks drift every ~5 s. */
  tick() {
    this.ticks++;
    this.sample();
    const st = this._view.status;
    if (st === 'waiting' && this.play && this.o.serverNow() >= this.play.startsAtMs) {
      this.syncNow();
      return;
    }
    if (this.ticks % 5 === 0) this.checkDrift();
  }

  /** Remember where the player was, to tell user seeks apart from buffering stalls. */
  sample() {
    if (this.o.adapter.state() === 'playing')
      this.lastSample = { pos: this.o.adapter.currentTimeMs(), at: this.o.localNow() };
  }

  checkDrift() {
    if (!this.play) return;
    const desired = desiredPositionMs(this.timing(), this.o.serverNow());
    const actual = this.o.adapter.currentTimeMs();
    const decision = this.drift.evaluate({
      nowMs: this.o.localNow(),
      following:
        this._view.following &&
        this._view.status !== 'unsynced' &&
        this._view.status !== 'suspended',
      visible: this.o.isVisible(),
      blocked: this._view.status === 'blocked',
      errored: this._view.status === 'error',
      playerState: this.o.adapter.state(),
      actualMs: actual,
      desiredMs: desired,
    });
    this.setView({ driftMs: desired === null ? null : Math.round(actual - desired) });
    if (decision.action === 'seek') {
      this.expect();
      this.o.adapter.seek(decision.toMs);
    }
  }

  onState(s: PlayerState) {
    if (!this.play) return;
    const st = this._view.status;
    if (s === 'playing') {
      if (
        !this.expecting() &&
        this._view.following &&
        (st === 'synced' || st === 'buffering') &&
        this.lastSample
      ) {
        // A user seek jumps the position; a network stall only lags behind it.
        const elapsed = this.o.localNow() - this.lastSample.at;
        const actual = this.o.adapter.currentTimeMs();
        const slack = this.o.userSeekThresholdMs ?? 2_500;
        if (
          actual > this.lastSample.pos + elapsed + slack ||
          actual < this.lastSample.pos - slack
        ) {
          this.markUnsynced();
          return;
        }
      }
      if (st === 'suspended' && !this.expecting()) {
        // User pressed play in the embed after returning: treat as local, unsynced listening.
        this.markUnsynced();
        return;
      }
      this.setView({ status: this._view.following ? 'synced' : 'unsynced' });
      if (this._view.following && st !== 'synced') this.o.report('synced', this.play.playId);
    } else if (s === 'buffering') {
      if (this._view.following && st !== 'unsynced' && st !== 'suspended')
        this.setView({ status: 'buffering' });
    } else if (s === 'paused') {
      // A pause we asked for is ours; any other pause (even right after Enable/Sync) is the user's.
      if (
        (this.expecting() && this.pauseExpected) ||
        st === 'suspended' ||
        st === 'waiting' ||
        st === 'needs-consent'
      )
        return;
      if (this.o.isVisible()) this.markUnsynced();
    } else if (s === 'ended') {
      // While waiting for a scheduled start the player holds the next video cued; an 'ended'
      // then can only belong to the previous video and must not strand us in 'finished'.
      if (st === 'waiting' || st === 'loading') return;
      if (st !== 'unsynced') this.setView({ status: 'finished' });
    }
  }

  private markUnsynced() {
    this.setView({ status: 'unsynced', following: false });
    if (this.play) this.o.report('unsynced', this.play.playId);
  }

  onAutoplayBlocked() {
    this.setView({ status: 'blocked' });
    if (this.play && this.reportedBlocked !== this.play.playId) {
      this.reportedBlocked = this.play.playId;
      this.o.report('autoplay_blocked', this.play.playId);
    }
  }

  onError(code: number) {
    this.setView({ status: 'error', errorCode: code });
    if (this.play) this.o.report('error', this.play.playId, code);
  }
}
